import { useEffect, useMemo, useState } from "react";
import { ORDER_STATUSES } from "../../../../types/order";
import { generateAddressElement } from "../../../../shared-utils/location";
import { Button, ButtonLink } from "../Button";
import { ArtList } from "../ArtList";
import { ShipmentDetails } from "./ShipmentDetails";
import { OrderInfoProps } from "./OrderInfo";
import { Api } from "../../api";
import { ArtData, SITE_DATA_PARTITIONS } from "../../../../types";
import { Input } from "../forms";
import { ART_URL, NEW_SLUG, ORDER_URL, SHIPMENT_URL } from "../../config";

export function OrderDetails({
  order,
  shipmentsRequest,
  showViewOrderBtn,
  showCreateShipmentBtn,
  showViewShipmentBtn,
  showItems,
  showOriginalItems,
  showShipmentItems,
  admin,
}: Pick<
  OrderInfoProps,
  | "order"
  | "shipmentsRequest"
  | "showViewOrderBtn"
  | "showCreateShipmentBtn"
  | "showViewShipmentBtn"
  | "showItems"
  | "showShipmentItems"
  | "showOriginalItems"
  | "admin"
>) {
  const [edit, setEdit] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [otherArtLoading, setOtherArtLoading] = useState<boolean>(false);
  const [otherArt, setOtherArt] = useState<ArtData[]>([]);
  const [selectedOtherArt, setSelectedOtherArt] = useState<string | null>(null);
  useEffect(() => {
    if (edit) {
      setOtherArtLoading(true);
      Api.getAll<ArtData>({
        type: SITE_DATA_PARTITIONS.ART,
      })
        .then((response) => {
          const artItems =
            response.body?.items.filter(
              (art) => art.isPublic && !art.isInProgress && !art.orderSlug,
            ) ?? [];
          setOtherArt(artItems);
        })
        .finally(() => {
          setOtherArtLoading(false);
        });
    }
  }, [edit]);
  const shipmentAndArtInfo = useMemo(() => {
    if (
      shipmentsRequest?.status === "present" &&
      shipmentsRequest.shipments &&
      order
    ) {
      const artWithNoShipment = order.items.filter(
        (item) =>
          !shipmentsRequest.shipments!.some((ship) =>
            ship.items.find((shipItem) => shipItem.slug === item.slug),
          ),
      );
      const readyToShip = shipmentsRequest?.shipments?.filter(
        (ship) => !ship.isShipped,
      );
      const shipped = shipmentsRequest?.shipments?.filter(
        (ship) => ship.isShipped,
      );
      return [
        {
          title: "Art that needs to be shipped",
          art: artWithNoShipment,
          type: "art-left",
        },
        {
          title: "Ready to ship",
          shipments: readyToShip,
          type: "ready-to-ship",
        },
        { title: "Already shipped", shipments: shipped, type: "shipped" },
      ];
    } else {
      return [
        {
          title: "Art in order",
          art: order?.items ?? [],
          shipments: undefined,
          type: "art",
        },
      ];
    }
  }, [order?.items, shipmentsRequest?.status, shipmentsRequest?.shipments]);
  const artLeft = shipmentAndArtInfo.find(
    (item) => item.type === "art-left",
  )?.art;
  const orderStatusClass =
    order?.orderStatus === ORDER_STATUSES.PLACED.key ||
    order?.orderStatus === ORDER_STATUSES.IN_PROGRESS.key
      ? order?.createdAt &&
        new Date(order.createdAt).getTime() < new Date().getTime() - 10000000
        ? " border-textWarning bg-warning "
        : " border-textMain bg-warning "
      : " border-primary bg-secondary ";
  const removeFromOrder = async (artSlug: string) => {
    const inShipment = !shipmentAndArtInfo
      ?.find((shipment) => shipment.type === "art-left")
      ?.art?.find((art) => art.slug === artSlug);
    if (inShipment) {
      setErrors((errs) => [
        ...errs,
        "Please remove art from all shipments before removing from the order.",
      ]);
      return;
    }
    const newItems = order?.items.filter((item) => item.slug !== artSlug);
    await Api.update({
      type: SITE_DATA_PARTITIONS.ORDER,
      slug: order?.slug,
      items: newItems,
    });
    await Api.update({
      type: SITE_DATA_PARTITIONS.ART,
      slug: artSlug,
      orderSlug: "",
    });

    window.location.reload();
  };

  const addToOrder = async (art: ArtData) => {
    const newItems = [...(order?.items ?? []), art];

    await Api.update({
      type: SITE_DATA_PARTITIONS.ORDER,
      slug: order?.slug,
      items: newItems,
    });
    await Api.update({
      type: SITE_DATA_PARTITIONS.ART,
      slug: art.slug,
      orderSlug: order?.slug,
    });

    window.location.reload();
  };
  return order ? (
    <div>
      <div className="min-h-[20px] z-[1000] w-full md:w-1/2 sticky top-4 right-4">
        {errors.map((e, idx) => (
          <div
            className="border-2 border-textWarning bg-white p-2 rounded-xl my-2 flex justify-around"
            key={e}
          >
            <div className="flex-1 basis-9/12 m-1">{e}</div>
            <div className="flex-1 basis-1/12 align-center">
              <Button
                btnType="nav"
                btnColor="warning"
                className="w-[64px] h-[64px] text-3xl !p-0"
                onClick={() => {
                  setErrors((errors) => errors.filter((_e, i) => idx != i));
                }}
              >
                🗑️
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div
        className={"px-5 py-2 my-2 border-solid border-2 " + orderStatusClass}
      >
        <h1 className="font-bold">Active Order #{order.slug}</h1>

        <div className="flex flex-col-reverse md:flex-row justify-start my-2">
          <div className="basis-6/12">
            <div className="font-bold">Customer email</div>
            <div>
              {order?.contactEmail
                ? order.contactEmail
                : "Contact support to see if we can get this lack of email sorted out"}
            </div>
            <div className="font-bold">Delivery address</div>
            <div>
              <i>
                {order?.deliveryLocation
                  ? generateAddressElement(order?.deliveryLocation)
                  : "Contact user to get delivery location"}
              </i>
            </div>

            <div className="font-bold">Order status</div>
            <div>
              <i>
                {Object.values(ORDER_STATUSES).find(
                  (statGlob) => statGlob.key === order?.orderStatus,
                )?.label ?? "No order status"}
              </i>
            </div>

            {showViewOrderBtn ? (
              <div>
                <ButtonLink
                  btnType="small"
                  btnColor="primary"
                  href={ORDER_URL(order.slug, admin)}
                >
                  View order
                </ButtonLink>
              </div>
            ) : null}
          </div>

          <div className="basis-2/12">
            <div className="font-bold">Created</div>
            <div>
              {order.createdAt
                ? Intl.DateTimeFormat("en-us", {
                    hour12: true,
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                  }).format(new Date(order.createdAt))
                : "No created at date"}
            </div>

            <div className="font-bold">Payment id</div>
            <div>{order?.pi ? order.pi : "No Payment intent on file"}</div>

            <div className="font-bold">Checkout</div>
            <div>
              {order?.checkoutId ? order.checkoutId : "No checkoutId on file"}
            </div>
          </div>
        </div>
        {showItems ? (
          <div>
            {shipmentAndArtInfo.map((content) =>
              content.art ? (
                <>
                  <h2 className="font-bold text-md">{content.title}</h2>
                  <ArtList
                    items={content.art}
                    noArt={
                      <>
                        {content.type === "art-left"
                          ? "No art left to add to shipments"
                          : "No art in order!"}
                      </>
                    }
                    getArtConfig={(art) => ({
                      height: 100,
                      hoverHeight: 200,
                      // href: ART_URL(art.slug, admin),
                      tileClassName:
                        content.type === "art-left"
                          ? " bg-main text-textSecondary rounded-2xl border-solid border-2 border-textWarning shadow-md hover:shadow-lg"
                          : undefined,
                      actionBelow:
                        edit && shipmentsRequest?.status === "present"
                          ? {
                              actionComponent: (
                                <Button
                                  btnType="small"
                                  btnColor="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();

                                    removeFromOrder(art.slug);
                                  }}
                                >
                                  Remove from order
                                </Button>
                              ),
                            }
                          : undefined,
                    })}
                  />
                </>
              ) : null,
            )}
            {showOriginalItems ? (
              <>
                <h2 className="font-bold text-md">Original items</h2>
                <ArtList
                  items={order.originalItems}
                  noArt={<>"No original items in order!"</>}
                  getArtConfig={(art) => ({
                    height: 100,
                    hoverHeight: 200,
                    // href: ART_URL(art.slug, admin),
                    // tileClassName:
                    // content.type === "art-left"
                    // ? " bg-main text-textSecondary rounded-2xl border-solid border-2 border-textWarning shadow-md hover:shadow-lg"
                    // : undefined,
                    // actionBelow:
                    //   edit && shipmentsRequest?.status === "present"
                    //     ? {
                    //         actionComponent: (
                    //           <Button
                    //             btnType="small"
                    //             btnColor="primary"
                    //             onClick={(e) => {
                    //               e.stopPropagation();
                    //               e.preventDefault();

                    //               removeFromOrder(art.slug);
                    //             }}
                    //           >
                    //             Remove from order
                    //           </Button>
                    //         ),
                    //       }
                    //     : undefined,
                  })}
                />
              </>
            ) : null}

            {edit ? (
              <div>
                <Button
                  btnType="small"
                  btnColor="primary"
                  disabled={
                    !otherArt.find((art) => art.slug === selectedOtherArt)
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    const art = otherArt.find(
                      (art) => art.slug === selectedOtherArt,
                    );
                    if (art) addToOrder(art);
                  }}
                >
                  Add item to order
                </Button>
                <Input
                  label="Art"
                  name="artToAdd"
                  className="!my-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  disabled={otherArtLoading}
                  placeholder="Pick art to add"
                  value={selectedOtherArt ?? ""}
                  onChange={(selection) => {
                    const val = selection.target.value;
                    setSelectedOtherArt(val);
                  }}
                  inputType="select"
                >
                  <option value="">Select an art to add</option>
                  {otherArt.map((art) => (
                    <option key={art.slug} value={art.slug}>
                      {art.title} ({art.slug})
                    </option>
                  ))}
                </Input>
              </div>
            ) : null}
          </div>
        ) : null}

        {showCreateShipmentBtn && artLeft?.length ? (
          <div>
            <ButtonLink
              btnType="small"
              btnColor="primary"
              href={SHIPMENT_URL(NEW_SLUG, admin, order.slug)}
            >
              Create shipment
            </ButtonLink>
          </div>
        ) : null}

        {admin ? (
          <Button
            btnType="small"
            btnColor="primary"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();

              setEdit(!edit);
            }}
          >
            {edit ? "Stop editing" : "Edit order"}
          </Button>
        ) : null}
        {shipmentAndArtInfo.map((item) =>
          item.shipments?.length ? (
            <div key={item.title}>
              <h2 className={"font-bold"}>{item.title}</h2>

              {item.shipments.map((shipment) => (
                <div key={shipment.slug}>
                  <ShipmentDetails
                    shipment={shipment}
                    admin={admin}
                    showItems={showShipmentItems}
                    showViewShipmentBtn={showViewShipmentBtn}
                    className={
                      item.type === "shipped"
                        ? " border-primary bg-main"
                        : item.type === "ready-to-ship"
                          ? " border-textWarning bg-main"
                          : undefined
                    }
                  />
                </div>
              ))}
            </div>
          ) : null,
        )}
      </div>
    </div>
  ) : (
    <div className="px-5 py-2 my-2 border-solid border-2 border-primary bg-accent">
      NO ORDER
    </div>
  );
}
